import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCompany } from "../../contexts/CompanyContext";
import { pointInAnyPolygon } from "../../lib/geo";
import { api, money, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Calendar } from "../../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Check, Minus, Plus, Home, Building2, Briefcase, Store, Tag, ShoppingBag, ChevronLeft, Banknote } from "lucide-react";
import { toast } from "sonner";
import { companyStatus, slotsForDate, isOpenDay } from "../../lib/hours";
import { enabledPaymentMethods, paymentMethodLabel } from "../../lib/paymentMethods";
import PaymentLogo from "../../components/icons/PaymentLogo";

const steps = ["Producto", "Dirección", "Cuándo", "Pago", "Confirmar"];

// Icon per address type — mirrors the Addresses.jsx type picker. Fallback to
// Home when the saved address predates the type field.
const ADDR_TYPE_ICON = {
  casa: Home,
  departamento: Building2,
  oficina: Briefcase,
  negocio: Store,
  // Legacy values from earlier iterations.
  hotel: Store,
  otro: Tag,
};
const addressIcon = (a) => ADDR_TYPE_ICON[a?.type] || Home;

// Round a number to at most `decimals` decimal places, dropping trailing zeros.
function compactNumber(n, decimals = 2) {
  if (!n) return "";
  const p = 10 ** decimals;
  const r = Math.round(n * p) / p;
  return Number.isInteger(r)
    ? String(r)
    : r.toFixed(decimals).replace(/0+$/, "").replace(/\.$/, "");
}

// Money formatter: "$1,234.50"
function fmtCurrency(n) {
  const v = Number(n) || 0;
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Cap user-typed numeric input to max `decimals` decimals. Also strips any
// non-digit/dot chars.
function sanitizeDecimalString(raw, decimals) {
  const cleanChars = raw.replace(/[^0-9.]/g, "");
  const parts = cleanChars.split(".");
  if (parts.length === 1) return parts[0];
  const tail = parts.slice(1).join("");
  if (decimals <= 0) return parts[0]; // integers only — drop the dot
  return `${parts[0]}.${tail.slice(0, decimals)}`;
}

/** Plain-text numeric input with spinner arrows hidden. */
function QtyInput({ value, onChange, testId, decimals = 2 }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState("");
  useEffect(() => {
    if (!focused) setRaw(compactNumber(value, decimals));
  }, [value, focused, decimals]);
  return (
    <input
      type="text"
      inputMode={decimals > 0 ? "decimal" : "numeric"}
      value={focused ? raw : compactNumber(value, decimals)}
      onFocus={() => { setFocused(true); setRaw(compactNumber(value, decimals)); }}
      onBlur={() => { setFocused(false); onChange(Number(raw) || 0); }}
      onChange={(e) => {
        const clean = sanitizeDecimalString(e.target.value, decimals);
        setRaw(clean);
        onChange(Number(clean) || 0);
      }}
      className="no-spinner h-9 w-24 rounded-md border border-input bg-background px-3 text-center text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[color:var(--company-primary)]"
      data-testid={testId}
      placeholder="0"
    />
  );
}

/** Money input showing "$" prefix and comma/decimal formatting on blur. */
function MoneyInput({ value, onChange, testId }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState("");
  useEffect(() => {
    if (!focused) setRaw(value ? compactNumber(value, 2) : "");
  }, [value, focused]);
  const display = focused
    ? (raw ? `$${raw}` : "$")
    : (value ? fmtCurrency(value) : "$0.00");
  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={() => { setFocused(true); setRaw(value ? compactNumber(value, 2) : ""); }}
      onBlur={() => { setFocused(false); onChange(Number(raw) || 0); }}
      onChange={(e) => {
        // Strip $ and any non-digit/dot, then cap at 2 decimals.
        const clean = sanitizeDecimalString(e.target.value, 2);
        setRaw(clean);
        onChange(Number(clean) || 0);
      }}
      className="no-spinner h-9 w-24 rounded-md border border-input bg-background px-2 text-center text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[color:var(--company-primary)]"
      data-testid={testId}
      placeholder="$0.00"
    />
  );
}

export default function NewOrder() {
  const { currentCompany, companies, setCurrentCompany } = useCompany();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  // Draft key scoped per company so switching companies doesn't leak carts.
  const DRAFT_KEY = `gas2go:orderDraft:${currentCompany?.company_id || "anon"}`;
  const freshStart = sp.get("fresh") === "1";
  // Lazily hydrate state from any saved draft in sessionStorage, unless the
  // user explicitly started a fresh wizard via ?fresh=1 (from Home CTA).
  const savedDraft = (() => {
    if (freshStart) {
      try { sessionStorage.removeItem(DRAFT_KEY); } catch (_e) {}
      return null;
    }
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_e) { return null; }
  })();

  const [step, setStep] = useState(savedDraft?.step ?? 0);
  const [products, setProducts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [cart, setCart] = useState(savedDraft?.cart || {}); // product_id -> qty
  const [addressId, setAddressId] = useState(savedDraft?.addressId || "");
  const companyOpenStatus = companyStatus(currentCompany);
  const storeClosedNow = !companyOpenStatus.open;
  const tempClosed = !!currentCompany?.temporarily_closed;
  const [when, setWhen] = useState(savedDraft?.when ?? (storeClosedNow ? "scheduled" : "now"));
  const [scheduledDate, setScheduledDate] = useState(savedDraft?.scheduledDate ? new Date(savedDraft.scheduledDate) : null);
  const [scheduledTime, setScheduledTime] = useState(savedDraft?.scheduledTime ?? "10:00");
  const [payment, setPayment] = useState(savedDraft?.payment ?? "cash"); // cash | stripe | bbva | santander | hsbc
  const [notes, setNotes] = useState(savedDraft?.notes ?? "");
  const [placing, setPlacing] = useState(false);
  // Kind is the fundamental channel filter — it MUST be preserved across
  // navigation (Manage addresses, browser back, etc.). We read it from the
  // query string first, and fall back to the last value stored in the draft
  // so the user can never accidentally see products from another channel
  // mixed into their cart.
  const preSelectedKind = sp.get("kind") || savedDraft?.kind || null;
  const preSelectedProduct = sp.get("preselect");
  const preSelectedQty = parseInt(sp.get("qty") || "1", 10) || 1;
  const queryAddressId = sp.get("addr");
  // Explicit company override (e.g., from Home "Pedir otra vez" CTA where
  // we want to restart the wizard in the EXACT company of the last order).
  // Takes priority over the address `preferred_company_id` so reorders
  // always reproduce the previous purchase context.
  const queryCompanyId = sp.get("company");
  // Precio unitario del pedido anterior (para avisar al cliente si el
  // precio actual del catálogo de la empresa difiere). Se usa como
  // referencia pura — el total del pedido se recalcula siempre con el
  // precio vigente de la empresa.
  const lastUnitPrice = sp.get("last_price") ? Number(sp.get("last_price")) : null;
  // Lista compacta de items del último pedido (formato "pid:qty,pid:qty").
  // Usada por "Pedir otra vez" para precargar TODO el carrito, no sólo
  // el primer item. Si el string es inválido, el parse cae en arreglo
  // vacío y el wizard sigue funcionando como arranque normal.
  const queryItemsParam = sp.get("items");
  const queryItems = (() => {
    if (!queryItemsParam) return [];
    try {
      return queryItemsParam.split(",").map((pair) => {
        const [pid, qtyStr] = pair.split(":");
        const qty = Number(qtyStr);
        if (!pid || !Number.isFinite(qty) || qty <= 0) return null;
        return { product_id: pid, quantity: qty };
      }).filter(Boolean);
    } catch (_e) { return []; }
  })();
  // Return URL that preserves the kind so "Gestionar direcciones" → back
  // never loses the channel filter.
  const returnPath = preSelectedKind ? `/app/order/new?kind=${preSelectedKind}` : "/app/order/new";

  // Persist draft whenever user-editable state changes so navigating to
  // "Gestionar direcciones" and back doesn't wipe the cart/selections.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          step,
          cart,
          addressId,
          when,
          scheduledDate: scheduledDate ? scheduledDate.toISOString() : null,
          scheduledTime,
          payment,
          notes,
          kind: preSelectedKind,
        })
      );
    } catch (_e) { /* quota */ }
  }, [DRAFT_KEY, step, cart, addressId, when, scheduledDate, scheduledTime, payment, notes, preSelectedKind]);

  // Toast on first mount when we hydrated a non-empty draft for this
  // company. Helps users who switch companies (each has its own draft) feel
  // confident their progress was preserved. Includes a "Empezar de nuevo"
  // action that wipes the draft and resets the wizard. Skip on a fresh
  // start (?fresh=1) and on drafts that only contain default values.
  useEffect(() => {
    if (!currentCompany) return;
    if (freshStart) return;
    const hasItems = savedDraft && Object.values(savedDraft.cart || {}).some((q) => Number(q) > 0);
    const movedPastFirstStep = (savedDraft?.step ?? 0) > 0;
    if (!hasItems && !movedPastFirstStep) return;
    const kindLabel = savedDraft?.kind === "bulk"
      ? "Gas a granel"
      : savedDraft?.kind === "stationary"
      ? "Estaciones"
      : "Cilindros";
    const id = toast.info(`Retomamos tu pedido de ${kindLabel} en ${currentCompany.name}`, {
      duration: 6000,
      action: {
        label: "Empezar de nuevo",
        onClick: () => {
          try { sessionStorage.removeItem(DRAFT_KEY); } catch (_e) { /* ignore */ }
          setCart({});
          setStep(0);
          setAddressId("");
          setNotes("");
          toast.success("Pedido reiniciado");
        },
      },
    });
    return () => { try { toast.dismiss(id); } catch (_e) { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.company_id]);

  useEffect(() => {
    if (!currentCompany) return;
    api.get(`/products?company_id=${currentCompany.company_id}`).then((r) => {
      setProducts(r.data);
      // Preload del carrito — caso A: multi-item via ?items=pid:qty,pid:qty
      // (flujo "Pedir otra vez" desde Home). Caso B (legacy): single
      // ?preselect=<pid>&qty=<n>. Si ambos vienen, `items` gana porque
      // es el formato más rico.
      let cartSeed = null;
      if (queryItems.length > 0) {
        cartSeed = {};
        for (const it of queryItems) {
          const p = r.data.find((x) => x.product_id === it.product_id);
          if (p) cartSeed[p.product_id] = it.quantity;
        }
      } else if (preSelectedProduct) {
        const p = r.data.find((x) => x.product_id === preSelectedProduct);
        if (p) cartSeed = { [p.product_id]: preSelectedQty };
      }
      if (cartSeed && Object.keys(cartSeed).length > 0) {
        setCart((c) => (Object.keys(c).length ? c : cartSeed));
      }
      // Aviso si el precio del primer item del reorder cambió (toast
      // informativo; el monto se recalcula con precios vigentes).
      const firstPid = queryItems[0]?.product_id || preSelectedProduct;
      if (firstPid && lastUnitPrice != null && Number.isFinite(lastUnitPrice)) {
        const p = r.data.find((x) => x.product_id === firstPid);
        if (p) {
          const now = Number(p.price) || 0;
          const diff = Math.abs(now - lastUnitPrice);
          if (diff > 0.005 * lastUnitPrice && diff > 0.5) {
            const fmt = (v) => `$${Number(v).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
            const delta = now > lastUnitPrice ? "subió" : "bajó";
            toast.info(`El precio de ${p.name} ${delta}: antes ${fmt(lastUnitPrice)}, ahora ${fmt(now)}.`, { duration: 7000 });
          }
        }
      }
    });
    api.get("/addresses").then((r) => {
      setAddresses(r.data);
      // Uber-eats style: preselect the first address so Continue is immediately enabled.
      let idToUse = null;
      // IMPORTANTE: en el flujo de REORDER (preselect + qty presentes) el
      // cliente debe ver el paso 0 (Producto) para confirmar la cantidad
      // y ver el precio actualizado. NO saltamos al paso 1 (Dirección)
      // aunque `addr` venga en la URL. El address se usa sólo como
      // preselección interna.
      // Tampoco saltamos cuando el wizard arranca FRESCO (`?fresh=1`):
      // un atajo "Pedir aquí" desde Mis direcciones manda
      // `?kind=...&addr=...&fresh=1` con el carrito vacío, así que el
      // cliente debe ver primero el paso Producto para elegir qué
      // pedir, no caer en Dirección sin nada en el carrito.
      const isReorder = !!preSelectedProduct || queryItems.length > 0;
      // Priority: ?addr=<id> query param > localStorage > draft > first addr
      if (queryAddressId && r.data.some((a) => a.address_id === queryAddressId)) {
        idToUse = queryAddressId;
        if (!isReorder && !freshStart) setStep(1);
      }
      if (!idToUse) {
        try {
          const preset = localStorage.getItem("selectedAddressId");
          if (preset && r.data.some((a) => a.address_id === preset)) {
            idToUse = preset;
            if (!isReorder) setStep(1);
          }
        } catch (_e) { /* ignore */ }
      }
      try { localStorage.removeItem("selectedAddressId"); } catch (_e) {}
      if (!idToUse && addressId && r.data.some((a) => a.address_id === addressId)) {
        idToUse = addressId; // keep existing (from draft)
      }
      if (!idToUse && r.data.length > 0) idToUse = r.data[0].address_id;
      if (idToUse) setAddressId(idToUse);
    });
  }, [currentCompany, queryAddressId]);

  // Conmutación explícita de empresa vía ?company=<id> (por ejemplo
  // desde el CTA "Pedir otra vez" en Home, que arma la URL con la
  // empresa del último pedido). Esta toma prioridad sobre la empresa
  // preferida de la dirección para que el reorder reproduzca exacto
  // el pedido anterior.
  useEffect(() => {
    if (!queryCompanyId) return;
    if (!companies?.length || !currentCompany) return;
    if (queryCompanyId === currentCompany.company_id) return;
    const tgt = companies.find((c) => c.company_id === queryCompanyId);
    if (!tgt) return;
    setCurrentCompany(tgt);
    // Write directly to localStorage so the post-reload bootstrap picks
    // the correct company even if React hasn't flushed its effect yet.
    try { localStorage.setItem("selected_company_id", tgt.company_id); } catch (_e) {}
    try { sessionStorage.removeItem(DRAFT_KEY); } catch (_e) { /* ignore */ }
    toast.success(`Cargamos el catálogo de ${tgt.name}`);
    // Rebuild the URL preserving all the reorder context (addr, preselect,
    // qty, kind, last_price) but dropping `company` (we're now on it) so
    // the effect doesn't loop after the reload.
    const next = new URLSearchParams();
    if (preSelectedKind) next.set("kind", preSelectedKind);
    if (queryAddressId) next.set("addr", queryAddressId);
    if (preSelectedProduct) next.set("preselect", preSelectedProduct);
    if (sp.get("qty")) next.set("qty", sp.get("qty"));
    if (sp.get("items")) next.set("items", sp.get("items"));
    if (sp.get("last_price")) next.set("last_price", sp.get("last_price"));
    next.set("fresh", "1");
    window.location.assign(`/app/order/new?${next.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, queryCompanyId]);

  // NOTA: previamente había un useEffect que auto-conmutaba la empresa
  // cuando la dirección elegida tenía `preferred_company_id` distinto
  // a la activa. Eso causaba un bug: hacer "Usar" desde Gestionar
  // direcciones (con `?addr=<id>`) cambiaba la empresa sin que el
  // cliente lo pidiera. Eliminado a propósito — la ÚNICA forma de
  // cambiar de empresa es el clic explícito en el chip "Cambiar a X
  // empresa" del marketplace, que abajo (paso 1) reinicia el wizard
  // preservando `kind` (autotanque/cilindrera) y la dirección activa.

  const filtered = useMemo(() => {
    if (!preSelectedKind) return products;
    return products.filter((p) => p.kind === preSelectedKind);
  }, [products, preSelectedKind]);

  const total = useMemo(() => {
    return Object.entries(cart).reduce((sum, [pid, qty]) => {
      const p = products.find((x) => x.product_id === pid);
      if (!p) return sum;
      return sum + p.price * qty;
    }, 0);
  }, [cart, products]);

  // Resumen del carrito: [{product, qty, lineTotal}] — alimenta el
  // breadcrumb informativo que se muestra en todos los pasos del
  // wizard para que el cliente siempre sepa qué está comprando.
  const cartSummary = useMemo(() => {
    return Object.entries(cart)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([pid, qty]) => {
        const p = products.find((x) => x.product_id === pid);
        if (!p) return null;
        return { product: p, qty: Number(qty), lineTotal: p.price * qty };
      })
      .filter(Boolean);
  }, [cart, products]);

  const addQty = (p, delta) => {
    setCart((c) => {
      const cur = c[p.product_id] || 0;
      const next = Math.max(0, cur + delta);
      const n = { ...c };
      if (next === 0) delete n[p.product_id];
      else n[p.product_id] = next;
      return n;
    });
  };

  const setBulkQty = (p, qty) => {
    const n = Math.max(0, Number(qty) || 0);
    // Cylinders only accept integers (can't ship half a cylinder).
    const rounded = p.kind === "cylinder"
      ? Math.floor(n)
      : Math.round(n * 100) / 100;
    setCart((c) => ({ ...c, [p.product_id]: rounded }));
  };

  // For bulk products the client can also drive the cart via "monto" ($). qty
  // is the source of truth in `cart`; the amount is always derived from
  // qty * price. Use 4-decimal precision on qty so the displayed amount
  // matches exactly what the user typed (e.g. $100 -> $100.00, not $100.04).
  const setBulkAmount = (p, amount) => {
    const a = Math.max(0, Number(amount) || 0);
    const price = Number(p.price) || 0;
    const qty = price > 0 ? Math.round((a / price) * 10000) / 10000 : 0;
    setCart((c) => ({ ...c, [p.product_id]: qty }));
  };

  const addBulkAmount = (p, delta) => {
    const price = Number(p.price) || 0;
    const curQty = cart[p.product_id] || 0;
    const curAmount = curQty * price;
    const next = Math.max(0, curAmount + delta);
    const qty = price > 0 ? Math.round((next / price) * 10000) / 10000 : 0;
    setCart((c) => {
      const n = { ...c };
      if (qty === 0) delete n[p.product_id];
      else n[p.product_id] = qty;
      return n;
    });
  };

  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const maxDate = useMemo(() => {
    const d = new Date(today); d.setDate(d.getDate() + 14); return d;
  }, [today]);
  // When closed_today, the earliest selectable date is tomorrow.
  const minDate = useMemo(() => {
    const mode = currentCompany?.close_mode || (currentCompany?.temporarily_closed ? "today" : "none");
    if (mode === "today") {
      const d = new Date(today); d.setDate(d.getDate() + 1); return d;
    }
    return today;
  }, [today, currentCompany]);

  const availableSlots = useMemo(() => {
    // Generate 30-min slots inside the weekly hours for the chosen day.
    if (!scheduledDate) return [];
    const raw = slotsForDate(currentCompany, scheduledDate, 30).map((s) => s.label);
    if (raw.length === 0) return [];
    const sel = new Date(scheduledDate);
    sel.setHours(0, 0, 0, 0);
    if (sel.getTime() !== today.getTime()) return raw;
    // Today — drop past slots (plus a 60-min lead time buffer).
    const now = new Date();
    const cutoff = now.getHours() * 60 + now.getMinutes() + 60;
    return raw.filter((v) => {
      const [h, m] = v.split(":").map(Number);
      return h * 60 + m >= cutoff;
    });
  }, [scheduledDate, today, currentCompany]);

  // Keep scheduledTime valid whenever the set of available slots changes
  useEffect(() => {
    if (availableSlots.length === 0) {
      setScheduledTime("");
    } else if (!availableSlots.includes(scheduledTime)) {
      setScheduledTime(availableSlots[0]);
    }
  }, [availableSlots, scheduledTime]);

  // Company-wide minimum order amount enforcement. Blocks "Continuar" when
  // the cart total is below the configured threshold.
  const companyMin = Number(currentCompany?.min_order_amount || 0);
  const belowCompanyMin = companyMin > 0 && total > 0 && total + 1e-6 < companyMin;

  // Si el cliente eligió una dirección que cae fuera de los polígonos
  // de la empresa activa, bloqueamos "Continuar" del paso 1. Así no
  // avanza a Cuándo/Pago para terminar con un rechazo del backend.
  // La tarjeta de la dirección ya muestra el chip rojo "Fuera de
  // cobertura" + chips de empresas alternativas (marketplace).
  const selectedAddress = addresses.find((a) => a.address_id === addressId);
  const selectedOutOfCoverage = (() => {
    const polys = currentCompany?.service_area_polygons || [];
    if (!selectedAddress || polys.length === 0) return false;
    if (selectedAddress.lat == null || selectedAddress.lng == null) return false;
    return !pointInAnyPolygon(selectedAddress.lat, selectedAddress.lng, polys);
  })();

  const canContinue = {
    0: Object.keys(cart).length > 0 && !belowCompanyMin,
    1: !!addressId && !selectedOutOfCoverage,
    2: when === "scheduled" ? !!(scheduledDate && scheduledTime) : !storeClosedNow,
    3: !!payment,
  };

  const place = async () => {
    if (!currentCompany) return;
    setPlacing(true);
    try {
      let scheduled_at = null;
      if (when === "scheduled" && scheduledDate) {
        const [hh, mm] = scheduledTime.split(":").map(Number);
        const d = new Date(scheduledDate);
        d.setHours(hh, mm, 0, 0);
        scheduled_at = d.toISOString();
      }
      // Atribución UTM: si el cliente entró por un link con `?from=`
      // (típicamente desde el bot de WhatsApp), guardamos el origen en
      // el pedido para luego medir conversión. Se borra después de usar
      // para no atribuir pedidos futuros del mismo cliente al mismo
      // canal indefinidamente.
      let attribution = null;
      try {
        attribution = localStorage.getItem("gas2go:source") || null;
      } catch (_e) { /* ignore */ }

      const payload = {
        company_id: currentCompany.company_id,
        items: Object.entries(cart).map(([product_id, quantity]) => ({ product_id, quantity })),
        address_id: addressId,
        when,
        scheduled_at,
        payment_method: payment,
        notes,
        ...(attribution ? { source: attribution } : {}),
      };
      const { data: order } = await api.post("/orders", payload);
      if (attribution) {
        try { localStorage.removeItem("gas2go:source"); } catch (_e) { /* ignore */ }
      }
      try { sessionStorage.removeItem(DRAFT_KEY); } catch (_e) { /* ignore */ }
      if (payment === "online" || payment === "stripe") {
        const { data: checkout } = await api.post("/payments/checkout/session", {
          order_id: order.order_id,
          origin_url: window.location.origin,
        });
        window.location.href = checkout.url;
      } else {
        toast.success("Pedido creado");
        navigate(`/app/orders/${order.order_id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el pedido");
    } finally {
      setPlacing(false);
    }
  };

  if (!currentCompany) return null;

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <main className="flex-1 w-full max-w-3xl mx-auto px-4 md:px-6 py-6 pb-28 md:pb-24">
        {/* Stepper */}
        <div className="flex items-center gap-1 mb-6">
          {steps.map((s, i) => (
            <div key={s} className="flex-1 flex items-center gap-1">
              <div className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-company" : "bg-neutral-200"}`} />
            </div>
          ))}
        </div>
        <p className="text-xs uppercase tracking-widest text-neutral-500">Paso {step + 1} / {steps.length}</p>
        <h1 className="font-display text-3xl font-extrabold mt-1">{steps[step]}</h1>

        {/* Breadcrumb informativo del carrito — visible en TODOS los
            pasos cuando ya hay algo seleccionado. Sirve para que el
            cliente nunca pierda de vista qué está comprando, sin
            importar en qué paso del wizard esté. Al hacer click lleva
            al paso 0 (Producto) para editar cantidades/productos. */}
        {cartSummary.length > 0 && step > 0 && (
          <button
            type="button"
            onClick={() => setStep(0)}
            data-testid="wizard-cart-breadcrumb"
            className="mt-3 w-full flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-3 text-left hover:border-neutral-400 tap-fade"
          >
            <div
              className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
            >
              <ShoppingBag size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tu pedido</p>
              <p className="text-sm font-semibold truncate">
                {cartSummary.map((ci) => {
                  const q = ci.product.kind === "cylinder"
                    ? `${ci.qty}×`
                    : `${ci.qty} ${ci.product.unit === "kg" ? "kg" : "L"} de`;
                  return `${q} ${ci.product.name}`;
                }).join(" · ")}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Total</p>
              <p className="font-display font-extrabold text-sm tabular-nums">{money(total)}</p>
            </div>
            <ChevronLeft size={16} className="text-neutral-400 rotate-180 shrink-0" aria-hidden />
          </button>
        )}

        <div className="mt-6 bg-white rounded-2xl border border-neutral-200 p-5">
          <div key={step} className="step-enter">
          {step === 0 && (
            <div className="space-y-3">
              {companyMin > 0 && (
                <div
                  className={`rounded-xl border p-3 text-sm ${belowCompanyMin ? "border-red-300 bg-red-50 text-red-700" : "border-[color:var(--company-primary)]/30 bg-company/5 text-neutral-700"}`}
                  data-testid="company-min-banner"
                >
                  <p className="font-semibold">Pedido mínimo: {money(companyMin)}</p>
                  <p className="text-xs mt-0.5">
                    {belowCompanyMin
                      ? `Te faltan ${money(companyMin - total)} para alcanzar el mínimo.`
                      : total > 0
                        ? "Tu pedido cumple con el mínimo."
                        : "Agrega productos hasta alcanzar el monto mínimo."}
                  </p>
                </div>
              )}
              {filtered.map((p) => {
                const qty = cart[p.product_id] || 0;
                const unitLabel = p.kind === "cylinder"
                  ? "cil."
                  : p.unit === "kg" ? "kg" : "L";
                const amount = qty * (Number(p.price) || 0);
                return (
                  <div key={p.product_id} className="py-3 border-b last:border-0" data-testid={`product-card-${p.product_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold">{p.name}</p>
                        <p className="text-sm text-neutral-500">
                          {p.kind === "cylinder" ? `${p.size_kg} kg` : `por ${p.unit === "kg" ? "kg" : "litro"}`} • {money(p.price)}
                        </p>
                      </div>
                      {qty > 0 && (
                        <span className="font-display font-extrabold text-lg shrink-0">{money(amount)}</span>
                      )}
                    </div>

                    {/* Quantity stepper — uniform across cylinder & bulk */}
                    <div className="flex items-center justify-end gap-3 mt-3">
                      <span className="text-[11px] uppercase tracking-widest text-neutral-400 w-14 text-right">Cantidad</span>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-9 w-9 shrink-0"
                          onClick={() => addQty(p, p.kind === "cylinder" ? -1 : -5)}
                          data-testid={`qty-minus-btn-${p.product_id}`}
                          disabled={qty <= 0}
                        >
                          <Minus size={14} />
                        </Button>
                        <QtyInput
                          value={qty}
                          onChange={(v) => setBulkQty(p, v)}
                          decimals={p.kind === "cylinder" ? 0 : 2}
                          testId={p.kind === "cylinder" ? `qty-${p.product_id}` : `bulk-qty-input-${p.product_id}`}
                        />
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-9 w-9 shrink-0"
                          onClick={() => addQty(p, p.kind === "cylinder" ? 1 : 5)}
                          data-testid={`qty-plus-btn-${p.product_id}`}
                        >
                          <Plus size={14} />
                        </Button>
                        <span className="text-xs text-neutral-500 w-10 text-left">{unitLabel}</span>
                      </div>
                    </div>

                    {/* Amount stepper — bulk only (linked to quantity) */}
                    {p.kind === "bulk" && (
                      <div className="flex items-center justify-end gap-3 mt-2">
                        <span className="text-[11px] uppercase tracking-widest text-neutral-400 w-14 text-right">Monto</span>
                        <div className="flex items-center gap-1.5">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-9 w-9 shrink-0"
                            onClick={() => addBulkAmount(p, -100)}
                            data-testid={`amt-minus-btn-${p.product_id}`}
                            disabled={amount <= 0}
                          >
                            <Minus size={14} />
                          </Button>
                          <MoneyInput
                            value={amount}
                            onChange={(v) => setBulkAmount(p, v)}
                            testId={`bulk-amt-input-${p.product_id}`}
                          />
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-9 w-9 shrink-0"
                            onClick={() => addBulkAmount(p, 100)}
                            data-testid={`amt-plus-btn-${p.product_id}`}
                          >
                            <Plus size={14} />
                          </Button>
                          <span className="text-xs text-neutral-500 w-10 text-left" aria-hidden></span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {Object.keys(cart).length > 0 && (
                <div className="flex justify-between items-center pt-2 font-semibold">
                  <span>Total</span>
                  <span className="font-display text-2xl">{money(total)}</span>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              {selectedOutOfCoverage && (
                <div
                  className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                  data-testid="selected-out-of-coverage-banner"
                >
                  <p className="font-semibold">Esta dirección está fuera de cobertura</p>
                  <p className="text-xs mt-0.5">
                    {currentCompany?.name} no entrega en este punto. Elige otra dirección dentro del área o cambia a una empresa que sí cubra.
                  </p>
                </div>
              )}
              {addresses.length === 0 ? (
                <div className="text-sm text-neutral-500">
                  No tienes direcciones guardadas. <a href={`/app/addresses?return=${encodeURIComponent(returnPath)}`} className="font-semibold text-black underline">Agrega una</a>
                </div>
              ) : (
                <div
                  className="max-h-[52vh] overflow-y-auto overflow-x-hidden"
                  data-testid="address-options-scroll"
                >
                  <RadioGroup
                    value={addressId}
                    onValueChange={(newId) => {
                      // Sólo cambiar el addressId — NO conmutamos empresa
                      // automáticamente al elegir una dirección. El cliente
                      // sólo cambia de empresa cuando hace clic explícito
                      // en el chip "Cambiar a [empresa]" del banner del
                      // marketplace, para evitar conmutaciones accidentales
                      // al tocar otras partes de la tarjeta.
                      setAddressId(newId);
                    }}
                    className="gap-2 min-w-0"
                  >
                    {addresses.map((a) => {
                    const Icon = addressIcon(a);
                    const selected = addressId === a.address_id;
                    const polys = currentCompany?.service_area_polygons || [];
                    const hasCoords = a.lat != null && a.lng != null;
                    const outOfCoverage = polys.length > 0 && hasCoords
                      && !pointInAnyPolygon(a.lat, a.lng, polys);
                    // Empresas alternativas (excluyendo la activa) que
                    // también cubren este punto. SÓLO se muestran cuando
                    // la dirección está fuera de cobertura de la empresa
                    // activa — el marketplace es una salida de emergencia,
                    // no un comparador permanente.
                    const altCompanies = (outOfCoverage && hasCoords)
                      ? (companies || []).filter((c) =>
                          c.company_id !== currentCompany?.company_id
                          && (c.service_area_polygons || []).length > 0
                          && pointInAnyPolygon(a.lat, a.lng, c.service_area_polygons)
                        )
                      : [];
                    const altLabel = "Sí entrega aquí:";
                    return (
                      <label
                        key={a.address_id}
                        className={`flex items-start gap-3 p-3 sm:p-4 rounded-xl border cursor-pointer transition-all tap-fade overflow-hidden ${
                          selected
                            ? "border-[color:var(--company-primary)] bg-[color:var(--company-primary)]/5 shadow-sm"
                            : outOfCoverage
                              ? "border-red-200 bg-red-50/40 hover:border-red-300"
                              : "border-neutral-200 hover:border-neutral-400"
                        }`}
                        data-testid={`address-option-${a.address_id}`}
                      >
                        <RadioGroupItem value={a.address_id} className="shrink-0 mt-1" />
                        <div
                          className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                            selected
                              ? "bg-company text-[color:var(--company-primary-contrast)]"
                              : outOfCoverage
                                ? "bg-red-100 text-red-700"
                                : "bg-neutral-100 text-neutral-700"
                          }`}
                        >
                          <Icon size={18} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold truncate flex items-center gap-1.5 flex-wrap">
                            {a.label}
                            {outOfCoverage && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 shrink-0"
                                data-testid={`address-out-coverage-${a.address_id}`}
                              >
                                Fuera de cobertura
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-neutral-500 truncate">{a.full_address}</p>
                          {a.reference && (
                            <p className="text-xs text-neutral-400 truncate mt-0.5">{a.reference}</p>
                          )}
                          {altCompanies.length > 0 && (
                            <div
                              className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-neutral-600"
                              data-testid={`address-alt-companies-${a.address_id}`}
                            >
                              <span className="text-neutral-500">{altLabel}</span>
                              {altCompanies.map((c) => (
                                <button
                                  key={c.company_id}
                                  type="button"
                                  // Detener el bubble — el `<label>` envolvente
                                  // dispararía la selección del radio. Queremos
                                  // que el clic SOLO cambie la empresa y
                                  // reinicie el wizard, ya que cada empresa
                                  // tiene su propio catálogo de productos.
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCurrentCompany(c);
                                    try { localStorage.setItem("selected_company_id", c.company_id); } catch (_e) {}
                                    try { sessionStorage.removeItem(DRAFT_KEY); } catch (_e) { /* ignore */ }
                                    toast.success(`Empresa cambiada a ${c.name}. Reiniciamos el pedido para cargar su catálogo.`);
                                    // Reiniciar el wizard preservando el
                                    // canal (kind: cylinder/bulk) Y la
                                    // dirección que el cliente acaba de
                                    // tocar — si la cambiamos, el wizard
                                    // re-abriría sin dirección activa,
                                    // mala experiencia. Esa dirección
                                    // ahora SÍ está en cobertura porque
                                    // el clic la asoció a una empresa
                                    // que sí cubre.
                                    const params = new URLSearchParams();
                                    if (preSelectedKind) params.set("kind", preSelectedKind);
                                    params.set("addr", a.address_id);
                                    params.set("fresh", "1");
                                    window.location.assign(`/app/order/new?${params.toString()}`);
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-neutral-300 hover:border-neutral-500 hover:bg-neutral-50 transition font-semibold"
                                  data-testid={`new-order-switch-company-${a.address_id}-${c.company_id}`}
                                >
                                  <span
                                    className="h-2 w-2 rounded-full"
                                    style={{ background: c.primary_color || "#0A0A0A" }}
                                  />
                                  Cambiar a {c.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                  </RadioGroup>
                </div>
              )}
              <Button variant="outline" asChild className="w-full tap-fade"><a href={`/app/addresses?return=${encodeURIComponent(returnPath)}`} data-testid="manage-addresses-btn">Gestionar direcciones</a></Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {tempClosed && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="wizard-temp-closed">
                  <p className="font-semibold">Cerrado temporalmente</p>
                  <p className="text-xs mt-0.5">Las entregas inmediatas están pausadas. Puedes programar tu pedido para más tarde.</p>
                </div>
              )}
              {!tempClosed && storeClosedNow && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="wizard-closed-now">
                  <p className="font-semibold">{companyOpenStatus.reason}</p>
                  <p className="text-xs mt-0.5">
                    Entrega inmediata no disponible.
                    {companyOpenStatus.nextOpen
                      ? ` Puedes programar tu pedido a partir del ${companyOpenStatus.nextOpen.dayLabel} a las ${companyOpenStatus.nextOpen.time}.`
                      : " Programa tu pedido para un día abierto."}
                  </p>
                </div>
              )}
              <RadioGroup value={when} onValueChange={(v) => !(storeClosedNow && v === "now") && setWhen(v)}>
                <label
                  className={`flex items-center gap-3 p-4 rounded-xl border ${storeClosedNow ? "border-neutral-200 opacity-50 cursor-not-allowed" : "border-neutral-200 cursor-pointer"}`}
                  data-testid="when-now-btn"
                >
                  <RadioGroupItem value="now" disabled={storeClosedNow} />
                  <div>
                    <p className="font-semibold">Ahora</p>
                    <p className="text-sm text-neutral-500">
                      {tempClosed
                        ? "No disponible — cerrado temporalmente"
                        : storeClosedNow
                          ? "No disponible — fuera de horario"
                          : "Entrega inmediata (≈ 30 min)"}
                    </p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 cursor-pointer" data-testid="when-scheduled-btn">
                  <RadioGroupItem value="scheduled" />
                  <div>
                    <p className="font-semibold">Programar</p>
                    <p className="text-sm text-neutral-500">Elige fecha y hora disponibles</p>
                  </div>
                </label>
              </RadioGroup>
              {when === "scheduled" && (
                <div className="grid md:grid-cols-[auto_1fr] gap-4 items-start">
                  <div>
                    <Label>Fecha</Label>
                    <div className="rounded-2xl border border-neutral-200 p-2 mt-1 bg-white" data-testid="scheduled-date-input">
                      <Calendar
                        mode="single"
                        selected={scheduledDate}
                        onSelect={setScheduledDate}
                        disabled={(d) => d < minDate || d > maxDate || !isOpenDay(currentCompany, d)}
                      />
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">Hasta 14 días adelante. Los días cerrados aparecen inactivos.</p>
                  </div>
                  <div>
                    <Label>Hora de entrega</Label>
                    {!scheduledDate ? (
                      <div className="mt-1 rounded-2xl border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500" data-testid="scheduled-time-empty">
                        Elige una fecha para ver horarios disponibles
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div className="mt-1 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" data-testid="scheduled-time-none">
                        No quedan horarios disponibles ese día. Elige otra fecha.
                      </div>
                    ) : (
                      <div className="mt-1 rounded-2xl border border-neutral-200 p-3 bg-white">
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1" data-testid="scheduled-time-grid">
                          {availableSlots.map((v) => {
                            const selected = v === scheduledTime;
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setScheduledTime(v)}
                                className={`h-10 rounded-lg border text-sm font-semibold transition ${
                                  selected
                                    ? "bg-company text-[color:var(--company-primary-contrast)] border-company shadow-sm"
                                    : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50"
                                }`}
                                data-testid={`scheduled-slot-${v}`}
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-neutral-500 mt-2">Slots de 30 minutos según el horario configurado.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <PaymentMethodPicker
                company={currentCompany}
                value={payment}
                onChange={setPayment}
              />
              <div>
                <Label>Notas para la unidad de reparto (opcional)</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej: Timbre 3B" className="h-12" data-testid="order-notes" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-display font-bold text-lg">Resumen</h3>
              <div className="space-y-2 text-sm">
                {Object.entries(cart).map(([pid, qty]) => {
                  const p = products.find((x) => x.product_id === pid);
                  if (!p) return null;
                  return (
                    <div key={pid} className="flex justify-between">
                      <span>{p.name} × {qty}</span>
                      <span>{money(p.price * qty)}</span>
                    </div>
                  );
                })}
                <div className="flex justify-between font-bold font-display text-lg border-t pt-2">
                  <span>Total</span><span>{money(total)}</span>
                </div>
              </div>
              <div className="text-sm text-neutral-600 space-y-1">
                <p><span className="text-neutral-400">Entrega:</span> {when === "now" ? "Ahora" : `${scheduledDate?.toLocaleDateString("es")} a las ${scheduledTime}`}</p>
                <p><span className="text-neutral-400">Pago:</span> {paymentMethodLabel(payment)}</p>
                <p><span className="text-neutral-400">Dirección:</span> {addresses.find((a) => a.address_id === addressId)?.full_address}</p>
              </div>
            </div>
          )}
          </div>
        </div>
      </main>

      {/* Sticky bottom navigation — keeps Cancelar / Atrás / Continuar in
          the same screen position across all wizard steps. The buttons
          stick to the viewport bottom while the parent flex column is in
          view, with a translucent background so content scrolling under
          the bar is hinted at, not abruptly cut off. */}
      <div
        className="sticky bottom-0 z-30 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-lg border-t border-neutral-200 dark:border-neutral-800 pb-[env(safe-area-inset-bottom)]"
        data-testid="wizard-nav"
      >
        <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-3 flex gap-3">
          <Button
            variant="ghost"
            className="h-12 text-neutral-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-neutral-400 dark:hover:text-red-300"
            onClick={() => {
              try { sessionStorage.removeItem(DRAFT_KEY); } catch (_e) { /* ignore */ }
              navigate("/app");
            }}
            data-testid="cancel-order-btn"
          >
            Cancelar
          </Button>
          {step > 0 && (
            <Button
              variant="outline"
              className="flex-1 h-12 tap-fade dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-700"
              onClick={() => setStep(step - 1)}
              data-testid="back-btn"
            >
              Atrás
            </Button>
          )}
          {step < 4 && (
            <Button
              variant="default"
              style={!canContinue[step] ? undefined : { background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
              className="flex-1 h-12 rounded-xl font-semibold disabled:bg-neutral-200 disabled:text-neutral-400 dark:disabled:bg-neutral-800 dark:disabled:text-neutral-500 disabled:opacity-100 hover:opacity-90 tap-fade"
              disabled={!canContinue[step]}
              onClick={() => setStep(step + 1)}
              data-testid="order-continue-btn"
            >
              Continuar
            </Button>
          )}
          {step === 4 && (
            <Button
              variant="default"
              style={placing ? undefined : { background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
              className="flex-1 h-12 rounded-xl font-semibold disabled:bg-neutral-200 disabled:text-neutral-400 dark:disabled:bg-neutral-800 dark:disabled:text-neutral-500 disabled:opacity-100 hover:opacity-90 tap-fade"
              onClick={place}
              disabled={placing}
              data-testid="place-order-btn"
            >
              {placing ? "Creando..." : ((payment === "online" || payment === "stripe") ? "Pagar y confirmar" : "Confirmar pedido")} <Check size={18} className="ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Selector de método de pago — muestra solo los métodos habilitados
 * por la empresa (configurables en el panel admin → Configuración).
 *
 * Cada opción es una row con el logo a la izquierda (logo oficial del
 * banco/Stripe via simpleicons.org CDN, o icono lucide-react para
 * efectivo). El método seleccionado se resalta con el color primario
 * de la empresa.
 *
 * Si la empresa no tiene métodos configurados, default = ["cash", "stripe"].
 */
function PaymentMethodPicker({ company, value, onChange }) {
  const options = enabledPaymentMethods(company);
  // Si el método seleccionado dejó de estar disponible (ej. admin lo
  // deshabilitó mientras el cliente tenía un draft), seleccionamos el
  // primero disponible automáticamente. Asegura que el wizard nunca
  // intente enviar un método inválido al backend.
  React.useEffect(() => {
    if (!options.length) return;
    const ids = options.map((o) => o.id);
    const normalizedValue = value === "online" ? "stripe" : value;
    if (!ids.includes(normalizedValue)) {
      onChange(options[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.payment_methods, options.length]);

  if (options.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Esta empresa aún no ha configurado métodos de pago. Contacta al administrador.
      </div>
    );
  }

  return (
    <RadioGroup value={value} onValueChange={onChange}>
      {options.map((m) => {
        const selected = (value === "online" ? "stripe" : value) === m.id;
        return (
          <label
            key={m.id}
            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
              selected
                ? "border-company bg-[color:var(--company-primary)]/5"
                : "border-neutral-200 hover:border-neutral-300"
            }`}
            data-testid={`pay-${m.id}-btn`}
          >
            <RadioGroupItem value={m.id} />
            <div className="h-12 w-[88px] rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 p-1">
              {m.id === "cash" ? (
                <Banknote size={32} className="text-emerald-600" />
              ) : (
                <PaymentLogo id={m.id} size={32} maxWidth={80} />
              )}
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate">{m.label}</p>
              <p className="text-sm text-neutral-500 truncate">{m.description}</p>
            </div>
          </label>
        );
      })}
    </RadioGroup>
  );
}
